
import {from as observableFrom } from 'rxjs';

import {finalize, filter, map, mergeMap} from 'rxjs/operators';
import { map as _map, each, reduce, remove, find } from 'lodash';

import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { OrganigrammeService } from './organigramme.service';
import { NotifierService, DialogService } from '../ui';

import { NodeSide, OrganigrammeNode, OrganigrammePoint } from './model/node';
import { OrganigrammeLink } from './model/link';
import { OrganigrammeEntity } from './model/entity';

import {
  MIN_ZOOM,
  MAX_ZOOM,
  ZOOM_STEP,
  NODE_LAYOUT_GAP_X,
  NODE_LAYOUT_GAP_Y,
  NODE_DEFAULT_HEIGHT,
  NODE_RADIUS,
  forceInsideRange,
  GRID_CELL_WIDTH,
  GRID_CELL_HEIGHT,
  GRID_SEGMENT_HEIGHT,
  GRID_SEGMENT_WIDTH,
  snapToGridX,
  snapToGridY
} from './organigramme.settings'

type ViewMode = 'dossier' | 'record';

@Component({
  templateUrl: './organigramme.component.html',
})
export class OrganigrammeComponent implements OnInit {
  
  mode: ViewMode
  title: string

  nodes: OrganigrammeNode[] = []
  links: OrganigrammeLink[] = []
  selectedNode: OrganigrammeNode = null
  editedEntity: OrganigrammeEntity = null

  // Viewport
  offsetX = 0
  offsetY = 0
  zoom = 1

  @ViewChild('chartView')
  chartView: ElementRef

  @ViewChild('drawings')
  drawings: ElementRef

  // D&D
  draggingMode : 'viewport' | 'node' | 'link-text' | 'link-polyline' = null
  draggingTarget: OrganigrammeNode | OrganigrammeLink = null
  draggingOccured = false
  draggingAxis: 'X' | 'Y' = null
  draggingAxisMin: number
  draggingAxisMax: number
  draggingPoints: OrganigrammePoint[]

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private organigrammeService: OrganigrammeService,
    private notifier: NotifierService,
    private dialogService: DialogService
  ) {}

  ngOnInit() {
    
    this.mode = this.route.routeConfig.data.mode;
    this.title = this.route.routeConfig.data.title;

    // Get the data
    this.notifier.startProgress();
    
    this.route.data.subscribe((data) => {
      
      let entity: OrganigrammeEntity = data.entity;
      let node = new OrganigrammeNode(entity);
      
      node.x = 0;
      node.y = 0;

      this.nodes.push(node);

      // Wraping inside setTimeout because of DOM latency
      setTimeout(() => {
        this.centerChart();
      }, 0);

      this.selectedNode = node;

      this.notifier.endProgress();
    });
  }

  // Viewport

  get drawingsBBox() {
    let $drawings: SVGGraphicsElement = this.drawings.nativeElement;
    return $drawings.getBBox();
  }

  get chartViewBCR() {
    let $chartView: HTMLElement = this.chartView.nativeElement;
    return $chartView.getBoundingClientRect();
  }

  centerChart() {
    let drawingsBBox = this.drawingsBBox;
    let chartViewBCR = this.chartViewBCR;
    
    const drawingsW = drawingsBBox.width;
    const drawingsH = drawingsBBox.height;
    const drawingsX = drawingsBBox.x;
    const drawingsY = drawingsBBox.y;

    const viewportW = chartViewBCR.width;
    const viewportH = chartViewBCR.height;

    this.offsetX =  (viewportW - drawingsW)/2 - drawingsX;
    this.offsetY = (viewportH - drawingsH)/2 - drawingsY;
  }

  get viewportHeight () {
    // Breadcrumb height = 56 px
    // Title = 60 px
    // Padding bottom = 24 px
    // Total 140 px
    return window.innerHeight - 140;
  }

  onResize(event: Event) {
    // Needed to force redraw on window size change
  }

  get backgroundOffsetX() {
    return (this.offsetX - (this.toScreenCoord(this.backgroundWidth) / 2)) % this.toScreenCoord(this.segmentW);
  }

  get backgroundOffsetY() {
    return (this.offsetY - (this.toScreenCoord(this.backgroundHeight) / 2)) % this.toScreenCoord(this.segmentH);
  }

  get backgroundWidth() {
    return Math.round(this.chartViewBCR.width / this.segmentW) * 4 * this.segmentW;
  }

  get backgroundHeight() {
    return Math.round(this.chartViewBCR.height / this.segmentH) * 4 * this.segmentH;
  }

  toDrawingCoord(value: number) {
    return value / (this.zoom * window.devicePixelRatio);
  }

  toScreenCoord(value: number) {
    return value * this.zoom * window.devicePixelRatio;
  }

  zoomIn() {
    let zoom = this.zoom + ZOOM_STEP;

    if(zoom > MAX_ZOOM) {
      zoom = MAX_ZOOM;
    }

    this.zoom = zoom;
  }

  zoomOut() {
    let zoom = this.zoom - ZOOM_STEP;
    
    if(zoom < MIN_ZOOM) {
      zoom = MIN_ZOOM;
    }

    this.zoom = zoom;
  }

  onMouseWheel(event: WheelEvent) {
    if(event.deltaY < 0) {
      this.zoomIn();
    }
    else if(event.deltaY > 0) {
      this.zoomOut();
    }
  }


  // Grid / Segment
  get gridH() {
    return GRID_CELL_HEIGHT;
  }

  get gridW() {
    return GRID_CELL_WIDTH;
  }

  get gridPath() {
    return `M ${this.gridW} 0 L 0 0 0 ${this.gridH}`;
  }

  get segmentH() {
    return GRID_SEGMENT_HEIGHT;
  }

  get segmentW() {
    return GRID_SEGMENT_WIDTH;
  }

  get segmentPath() {
    return `M ${this.segmentW} 0 L 0 0 0 ${this.segmentH}`;
  }

  // Drag & Drop (Viewport + Node)

  private isBackgroundTarget(target: EventTarget) {
    if(target instanceof SVGElement && target.classList.contains('background')) {
      return true
    }
    else if(target === this.chartView.nativeElement) {
      return true;
    }
    else {
      return false;
    }
  }

  onMouseDown(event: MouseEvent, target: OrganigrammeNode | OrganigrammeLink = null, elementType: string = null) {
    // Ignore non left mouse button clicks
    if(event.button !== 0) {
      return;
    }
    
    if(target instanceof OrganigrammeNode) {
      this.draggingMode = 'node';
      this.draggingTarget = target;
    }

    else if(target instanceof OrganigrammeLink) {
      if(elementType === 'text') {
        this.draggingMode = 'link-text';
      }
      else if(elementType === 'polyline') {
        this.draggingMode = 'link-polyline';

        let bcr = this.chartView.nativeElement.getBoundingClientRect();
        let mouseX = this.toDrawingCoord(event.clientX - bcr.left - this.offsetX);
        let mouseY = this.toDrawingCoord(event.clientY - bcr.top - this.offsetY);

        let firstPoint: OrganigrammePoint = null;
        let secondPoint: OrganigrammePoint = null;

        // Lookup the polyline segment
        for(let i = 0; i < target.points.length - 1; i++) {
          let p1 = target.points[i];
          let p2 = target.points[i + 1];

          // Ignore points if they are the same
          if(p1.x === p2.x && p1.y === p2.y) {
            continue;
          }

          // A error margin is necessary to make selection easy for the user
          const errorMargin = 2 * this.zoom;

          const minX = Math.min(p1.x, p2.x) - errorMargin;
          const maxX = Math.max(p1.x, p2.x) + errorMargin;
          const minY = Math.min(p1.y, p2.y) - errorMargin;
          const maxY = Math.max(p1.y, p2.y) + errorMargin;


          if(mouseX >= minX && mouseX <= maxX && mouseY >= minY && mouseY <= maxY) {
            firstPoint = p1;
            secondPoint = p2;

            break;
          }
        }

        if(!firstPoint || !secondPoint) {
          return;
        }

        // Detect the dragging axis
        if(firstPoint.x === secondPoint.x) {
          this.draggingAxis = 'X';
        }
        else if(firstPoint.y == secondPoint.y) {
          this.draggingAxis = 'Y';
        }


        // Build dragging points list
        this.draggingPoints = [firstPoint, secondPoint];

        if(target.segments.length === 2) { // Case when the middle points are the same (combined)
          if(secondPoint === target.middlePoint1) {
            this.draggingPoints.push(target.middlePoint2);
          }
          else if(firstPoint === target.middlePoint2) {
            this.draggingPoints.push(target.middlePoint1);
          }
        }


        // Calculate the dragging boundaries

        // First segment (from -> middle 1)
        if(firstPoint === target.fromPoint) {
          if(this.draggingAxis === 'X') {
            // Move within the width of the FROM node
            this.draggingAxisMin = target.from.x + NODE_RADIUS;
            this.draggingAxisMax = target.from.x + target.from.width - NODE_RADIUS;
          }
          else {
            // Move within the height of the FROM node
            this.draggingAxisMin = target.from.y + NODE_RADIUS;
            this.draggingAxisMax = target.from.y + target.from.height - NODE_RADIUS;
          }
        }
        // Second segment (middle 1 -> middle 2)
        else if(firstPoint === target.middlePoint1) {
          if(this.draggingAxis === 'X') {
            // Move between both nodes
            this.draggingAxisMin = Math.min(target.fromPoint.x, target.toPoint.x) + (2 * this.gridW);
            this.draggingAxisMax = Math.max(target.fromPoint.x, target.toPoint.x) - (2 * this.gridW);
          }
          else {
            // Move between both nodes
            this.draggingAxisMin = Math.min(target.fromPoint.y, target.toPoint.y) + (2 * this.gridH);
            this.draggingAxisMax = Math.max(target.fromPoint.y, target.toPoint.y) - (2 * this.gridH);
          }
        }
        // Third segment (middle 2 -> to)
        else if(firstPoint === target.middlePoint2) {
          if(this.draggingAxis === 'X') {
            // Move within the width of the TO node
            this.draggingAxisMin = target.to.x + NODE_RADIUS;
            this.draggingAxisMax = target.to.x + target.to.width - NODE_RADIUS;
          }
          else {
            // Move within the height of the FROM node
            this.draggingAxisMin = target.to.y + NODE_RADIUS;
            this.draggingAxisMax = target.to.y + target.to.height - NODE_RADIUS;
          }
        }
      }
      this.draggingTarget = target;
    }

    else if(this.isBackgroundTarget(event.target)) {
      this.draggingMode = 'viewport';
    }

    if(this.draggingMode) {
      event.preventDefault();
    }
  }

  onMouseMove(event: MouseEvent) {
    if(this.draggingMode) {
      event.preventDefault();

      if(this.draggingMode === 'viewport') {
        this.offsetX += this.toDrawingCoord(event.movementX);
        this.offsetY += this.toDrawingCoord(event.movementY);
      }
      else if(this.draggingMode === 'node' && this.draggingTarget instanceof OrganigrammeNode) {
        this.draggingTarget.x += this.toDrawingCoord(event.movementX);
        this.draggingTarget.y += this.toDrawingCoord(event.movementY);

        // Update connected links layout
        each(this.draggingTarget.links, l => l.layout());
      }
      else if(this.draggingTarget instanceof OrganigrammeLink) {
        if(this.draggingMode === 'link-text') {
          this.draggingTarget.nameX += this.toDrawingCoord(event.movementX);
          this.draggingTarget.nameY += this.toDrawingCoord(event.movementY);
        }
        else if(this.draggingMode === 'link-polyline') {
          if(this.draggingAxis === 'X') {
            let delta = this.toDrawingCoord(event.movementX);

            each(this.draggingPoints, p => {
              p.x = forceInsideRange( p.x + delta, this.draggingAxisMin, this.draggingAxisMax);
            });
          }
          else if(this.draggingAxis === 'Y') {
            let delta = this.toDrawingCoord(event.movementY);
            
            each(this.draggingPoints, p => {
              p.y = forceInsideRange( p.y + delta, this.draggingAxisMin, this.draggingAxisMax);
            });
          }

          this.draggingTarget.layoutArrow();
          this.draggingTarget.layoutName();
        }
      }

      this.draggingOccured = true;
    }
  }

  onMouseUp(event: MouseEvent) {
    // Simulate a click event to select a node
    if(this.draggingTarget instanceof OrganigrammeNode) {
      this.selectedNode = this.draggingTarget;
      this.editedEntity = null;

      this.draggingTarget.x = snapToGridX(this.draggingTarget.x);
      this.draggingTarget.y = snapToGridY(this.draggingTarget.y);

      // Update connected links layout
      each(this.draggingTarget.links, l => l.layout());
    }
    // Deselect any node if no dragging has occured
    else if(!this.draggingOccured && this.isBackgroundTarget(event.target)) {
      this.selectedNode = null;
      this.editedEntity = null;
    }

    if(this.draggingMode) {
      this.draggingMode = null;
      this.draggingTarget = null;
      this.draggingOccured = false;
      this.draggingAxis = null;
      this.draggingAxisMax = null;
      this.draggingAxisMin = null;
      this.draggingPoints = null;
      event.preventDefault();
    }
  }


  // Nodes / Links / Entities

  get entities() {
    return _map(this.nodes, n => n.entity);
  }

  entitiesAdded(entities: OrganigrammeEntity[]) {

    this.notifier.startProgress();

    this.editedEntity = null;

    let newNodes:OrganigrammeNode[] = [];

    // Add nodes
    each(entities, (e, index) => {
      let node = new OrganigrammeNode(e);
      newNodes.push(node);

      this.nodes.push(node);
    });

    this.layoutNewNodes(newNodes);

    
    // Add links
    let allEntities = this.entities;

    observableFrom(entities).pipe(

    // Get links for each target entity
    mergeMap(entity => {
      return this.organigrammeService
            .links(entity.dossier_id, entity.record_id).pipe(
            // Merge result + entity
            map(result => ({
              entity: entity,
              result: result
            })));
    }),

    // Remove links which point to unadded entities
    map(({entity, result}) => {
      
      remove(result.belongs_to, l => {
        return !find(allEntities, e => e.id === l.entity.id);
      });

      remove(result.has_shares_in, l => {
        return !find(allEntities, e => e.id === l.entity.id);
      });

      return {
        entity: entity,
        result: result,
      };
    }),

    // Create links
    mergeMap(({entity, result}) => {
      let links: OrganigrammeLink[] = [];

      let node = find(this.nodes, n => n.id === entity.id);

      // Create links
      each(result.belongs_to, l => {
        let to = node;
        let from = find(this.nodes, n => n.id === l.entity.id);

        let link = new OrganigrammeLink(from, to, l);
        links.push(link);
      });

      each(result.has_shares_in, l => {
        let to = find(this.nodes, n => n.id === l.entity.id);
        let from = node;

        let link = new OrganigrammeLink(from, to, l);
        links.push(link);
      });

      return links;
    }),

    // Filter duplicates & add unique links
    filter(l => {
      let found = find(this.links, link => link.id === l.id);

      if(found) {
        return false;
      }
      else {
        this.links.push(l);
        return true;
      }
    }),

    finalize(() => {
      this.notifier.endProgress();
  
      // Wraping inside setTimeout because of DOM latency
      setTimeout(() => {
        this.centerChart();
      }, 0);
    }))
    
    .subscribe(l => {
      // Add link to both from/to entities
      l.from.links.push(l);
      l.to.links.push(l);

      // Init link layout
      l.layout();
    });
  }

  layoutNewNodes(nodes: OrganigrammeNode[]) {
    let drawingsBBox = this.drawingsBBox;
    
    // const drawingsW = drawingsBBox.width;
    const drawingsH = drawingsBBox.height;
    const drawingsX = drawingsBBox.x;
    const drawingsY = drawingsBBox.y;

    let {x: targetCenterX, y: targetCenterY} = this.selectedNode.center;

    // X at the center of the current node
    let nodesTotalWidth = reduce(nodes, (w, n) => {
      return w + n.width;
    }, 0);
    
    nodesTotalWidth += ((nodes.length - 1) * NODE_LAYOUT_GAP_X);
    
    let startX = targetCenterX - (nodesTotalWidth / 2);

    // Y at the top or bottom of the current drawings
    const startYTop = snapToGridY(drawingsY - NODE_LAYOUT_GAP_Y);
    const startYBottom = snapToGridY(drawingsY + drawingsH + NODE_LAYOUT_GAP_Y);

    let startY = 0;

    if(Math.abs(startYTop - targetCenterY) <= Math.abs(startYBottom - targetCenterY)) {
      startY = startYTop - NODE_DEFAULT_HEIGHT;
    }
    else {
      startY = startYBottom;
    }

    each(nodes, (n, i) => {
      n.x = startX;
      n.y = startY;

      n.x = snapToGridX(n.x);
      n.y = snapToGridY(n.y);

      startX += n.width + NODE_LAYOUT_GAP_X;
    });
  }


  removeNode(node: OrganigrammeNode) {
    // Keep at least one node
    if(this.nodes.length <= 1) {
      return;
    }

    if(this.selectedNode === node) {
      this.selectedNode = null;
    }

    // Remove the node itself
    remove(this.nodes, n => n === node);

    // Remove all links
    each(node.links, link => {
      if(link.from !== node) {
        remove(link.from.links, l => l === link);
      }

      if(link.to !== node) {
        remove(link.to.links, l => l === link);
      }

      remove(this.links, l => l === link);
    });
  }


  // Export
  
  exportData() {
    return {
      gridCellWidth: GRID_CELL_WIDTH,
      gridCellHeight: GRID_CELL_HEIGHT,
      nodes: _map(this.nodes, n => n.exportData()),
      links: _map(this.links, l => l.exportData()),
    };
  }

  generatePowerPoint() {
    let data = this.exportData();

    this.organigrammeService.generatePowerPoint(data);
  }

  generateExcel() {
    let data = this.exportData();

    this.organigrammeService.generateExcel(data);
  }


  // Legend

  hasDossierType(type: string) {
    if(this.nodes) {
      for(let i = 0;i < this.nodes.length; i++) {
        if(this.nodes[i].dossier_type === type) {
          return true;
        }
      }
    }

    return false;
  }
}
